import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { ProductsPage, PageHeader, PageTitle } from "../../styles/ProductsStyles";
import { GetCategoryBrandsList, GetUnverificationsProducts, GetVerifyStatusOfProduct } from "../../common/CommonApi";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  BadgeCheck,
  Boxes,
  CheckCircle2,
  Eye,
  Image as ImageIcon,
  Layers3,
  X,
  XCircle
} from "lucide-react";

const OperationList = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [message, setMessage] = useState("");
  const [isVerify, setIsVerify] = useState(false);
  const [verifyModal, setVerifyModal] = useState(false);
  const [viewModal, setVeiwModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewProductModal, setViewProductModal] = useState({});
  const [allBrandsCategory, setAllBrandsCategory] = useState({});
  const [variantModal, setVariantModal] = useState(false);
  const [imageModal, setImageModal] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const adminDetails = useSelector((state) => state.auth);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const productsResponse = await GetUnverificationsProducts(id,'brand');
      if (productsResponse.data.message === "DATA_FOUND") {
        setProducts(productsResponse.data.data.products || []);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getVariantImages = (product) => product?.variants?.[0]?.varientImages || product?.images || [];
  const getTotalStock = (product) => (product?.variants || []).reduce((sum, variant) => sum + (Number(variant.quantity) || 0), 0);
  const formatValue = (value) => value || "Not added";
  const truncate = (text = "", length = 120) => {
    if (!text) return "No description added";
    return text.length > length ? `${text.slice(0, length)}...` : text;
  };

  const openVerifyModal = (product, verifyStatus) => {
    setSelectedProduct(product);
    setVerifyModal(true);
    setIsVerify(verifyStatus);
  };

  const openVeiwModal = async (product) => {
    setViewProductModal(product);
    setVeiwModal(true);
    const resp = await GetCategoryBrandsList(product.brandsId, product.category, product.subcategory, product.adminId);
    if (resp.data.message === "DATA_FOUND") {
      setAllBrandsCategory(resp.data.data);
    }
  };

  const confirmVerify = async (verifyStatus) => {
    const obj = {
      id: selectedProduct._id,
      verificationStatus: verifyStatus,
      adminId: adminDetails._id,
      verificationNote: verifyStatus ? "Verified" : message,
      vendorId: selectedProduct.adminId
    };
    await GetVerifyStatusOfProduct(obj);
    fetchData();
    setVerifyModal(false);
    setMessage("");
  };

  const openVariantModal = (product) => {
    setSelectedProduct(product);
    setVariantModal(true);
  };

  const openImageModal = (product, index) => {
    setSelectedProduct(product);
    setLightboxIndex(index);
    setImageModal(true);
  };

  const closeModal = () => {
    setVerifyModal(false);
    setVariantModal(false);
    setImageModal(false);
    setVeiwModal(false);
    setMessage("");
  };

  const selectedImages = getVariantImages(selectedProduct);
  const prevImage = () => setLightboxIndex((i) => (i === 0 ? selectedImages.length - 1 : i - 1));
  const nextImage = () => setLightboxIndex((i) => (i === selectedImages.length - 1 ? 0 : i + 1));

  return (
    <ProductsPage>
      <PageHeader>
        <PageHeading>
          <PageTitle>Products For Verification</PageTitle>
          <PageSubtitle>Review partner-submitted products, variants, and catalog details.</PageSubtitle>
        </PageHeading>
      </PageHeader>

      <Wrapper>
        <Table>
          <Header>
            <div>Product</div>
            <div>Pricing</div>
            <div>Inventory</div>
            <div>Status</div>
            <div>Actions</div>
          </Header>

          {loading && (
            <LoadingState>
              <img src="/assets/loading.gif" alt="Loading products" />
            </LoadingState>
          )}

          {!loading && products.length === 0 && (
            <EmptyState>
              <Boxes size={34} />
              <strong>No products found</strong>
              <span>This partner does not have products waiting here right now.</span>
            </EmptyState>
          )}

          {!loading && products.map((product) => {
            const images = getVariantImages(product);
            const firstVariant = product?.variants?.[0] || {};
            const totalStock = getTotalStock(product);

            return (
              <Row key={product._id}>
                <ProductCell>
                  <ProductMedia type="button" onClick={() => images.length && openImageModal(product, 0)}>
                    {images[0] ? (
                      <img src={images[0]} alt={product.name || "Product"} />
                    ) : (
                      <ImagePlaceholder>
                        <ImageIcon size={22} />
                      </ImagePlaceholder>
                    )}
                    {images.length > 1 && <ImageCount>+{images.length - 1}</ImageCount>}
                  </ProductMedia>
                  <ProductCopy>
                    <ProductName>{product.name}</ProductName>
                    <ProductDesc>{truncate(product.description)}</ProductDesc>
                  </ProductCopy>
                </ProductCell>

                <PriceStack>
                  {firstVariant?.disCountedprice ? (
                    <DiscountedPrice>Rs. {firstVariant.disCountedprice}</DiscountedPrice>
                  ) : (
                    <MutedText>No sale price</MutedText>
                  )}
                  {firstVariant?.price ? <Price>Rs. {firstVariant.price}</Price> : <MutedText>No price</MutedText>}
                </PriceStack>

                <InventoryCell>
                  <strong>{totalStock}</strong>
                  <span>{product?.variants?.length || 0} variants</span>
                </InventoryCell>

                <Status verified={product.verificationStatus}>
                  {product.verificationStatus ? <BadgeCheck size={14} /> : null}
                  {product.verificationStatus ? "Verified" : "Pending"}
                </Status>

                <Actions>
                  <IconButton type="button" title="Product details" aria-label="Product details" onClick={() => openVeiwModal(product)}>
                    <Eye size={16} />
                  </IconButton>
                  <IconButton type="button" title="View variants" aria-label="View variants" onClick={() => openVariantModal(product)}>
                    <Layers3 size={16} />
                  </IconButton>
                  <ActionButton type="button" onClick={() => openVerifyModal(product, true)}>
                    <CheckCircle2 size={15} />
                    Verify
                  </ActionButton>
                  <ActionButton type="button" danger onClick={() => openVerifyModal(product, false)}>
                    <XCircle size={15} />
                    Reject
                  </ActionButton>
                </Actions>
              </Row>
            );
          })}
        </Table>
      </Wrapper>

      {verifyModal && selectedProduct && (
        <ModalOverlay onClick={closeModal}>
          <Modal width="460px" onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalEyebrow>{isVerify ? "Verify Product" : "Reject Product"}</ModalEyebrow>
                <ModalTitle>{selectedProduct.name}</ModalTitle>
              </div>
              <CloseButton type="button" aria-label="Close verification modal" onClick={closeModal}>
                <X size={18} />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <PromptBox verified={isVerify}>
                {isVerify ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
                <span>
                  {isVerify
                    ? "Confirm that this product is ready to appear in the catalog."
                    : "Add a clear note so the partner knows what to fix."}
                </span>
              </PromptBox>
              {!isVerify && (
                <FieldGroup>
                  <FieldLabel>Rejection note</FieldLabel>
                  <TextArea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Enter reason for rejection" />
                </FieldGroup>
              )}
            </ModalBody>
            <ModalActions>
              <SecondaryButton type="button" onClick={closeModal}>Cancel</SecondaryButton>
              <PrimaryButton type="button" danger={!isVerify} onClick={() => confirmVerify(isVerify)}>
                {isVerify ? "Verify Product" : "Submit Note"}
              </PrimaryButton>
            </ModalActions>
          </Modal>
        </ModalOverlay>
      )}

      {variantModal && selectedProduct && (
        <ModalOverlay onClick={closeModal}>
          <Modal width="720px" onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalEyebrow>Variants</ModalEyebrow>
                <ModalTitle>{selectedProduct.name}</ModalTitle>
              </div>
              <CloseButton type="button" aria-label="Close variants modal" onClick={closeModal}>
                <X size={18} />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <VariantTable>
                <thead>
                  <tr>
                    <th>Size</th>
                    <th>Color</th>
                    <th>Quantity</th>
                    <th>SKU</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedProduct.variants.map((variant, index) => (
                    <tr key={index}>
                      <td>{formatValue(variant.size)}</td>
                      <td>{formatValue(variant.color)}</td>
                      <td>{formatValue(variant.quantity)}</td>
                      <td>{formatValue(variant.sku)}</td>
                    </tr>
                  ))}
                </tbody>
              </VariantTable>
            </ModalBody>
            <ModalActions>
              <SecondaryButton type="button" onClick={closeModal}>Close</SecondaryButton>
            </ModalActions>
          </Modal>
        </ModalOverlay>
      )}

      {viewModal && (
        <ModalOverlay onClick={closeModal}>
          <Modal width="900px" onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalEyebrow>Product Details</ModalEyebrow>
                <ModalTitle>{viewProductModal.name}</ModalTitle>
              </div>
              <CloseButton type="button" aria-label="Close product details" onClick={closeModal}>
                <X size={18} />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <DetailsSection>
                <SectionTitle>Catalog Information</SectionTitle>
                <InfoGrid>
                  <InfoItem><Label>Short Description</Label><Value>{formatValue(viewProductModal.shortDescription)}</Value></InfoItem>
                  <InfoItem wide><Label>Description</Label><Value>{formatValue(viewProductModal.description)}</Value></InfoItem>
                  <InfoItem wide><Label>Product Details</Label><Value>{formatValue(viewProductModal.productDetails)}</Value></InfoItem>
                  <InfoItem><Label>Created Date</Label><Value>{viewProductModal?.createdDate ? new Date(viewProductModal.createdDate).toLocaleString() : "Not added"}</Value></InfoItem>
                  <InfoItem><Label>HSN Tax Code</Label><Value>{formatValue(viewProductModal.HsnTaxCode)}</Value></InfoItem>
                  <InfoItem><Label>Style Tag</Label><Value>{formatValue(viewProductModal.styleTags)}</Value></InfoItem>
                  <InfoItem><Label>Care Instructions</Label><Value>{formatValue(viewProductModal.careInstructions)}</Value></InfoItem>
                  <InfoItem><Label>Season</Label><Value>{formatValue(viewProductModal.season)}</Value></InfoItem>
                  <InfoItem><Label>Product Added By</Label><Value>{formatValue(allBrandsCategory?.AdminDetails?.fullname)}</Value></InfoItem>
                </InfoGrid>
              </DetailsSection>

              <DetailsSection>
                <SectionTitle>Brand & Category</SectionTitle>
                <EntityGrid>
                  <EntityCard>
                    <EntityImage src={allBrandsCategory?.Brands?.brandImage} alt={allBrandsCategory?.Brands?.brandName || "Brand"} />
                    <div>
                      <Label>Brand</Label>
                      <EntityName>{formatValue(allBrandsCategory?.Brands?.brandName)}</EntityName>
                      <Value>{formatValue(allBrandsCategory?.Brands?.brandDescription)}</Value>
                    </div>
                  </EntityCard>
                  <EntityCard>
                    <EntityImage src={allBrandsCategory?.Category?.categoryImage} alt={allBrandsCategory?.Category?.categoryName || "Category"} />
                    <div>
                      <Label>Category</Label>
                      <EntityName>{formatValue(allBrandsCategory?.Category?.categoryName)}</EntityName>
                      <Value>{formatValue(allBrandsCategory?.Category?.description)}</Value>
                    </div>
                  </EntityCard>
                  <EntityCard>
                    <EntityImage src={allBrandsCategory?.SubCategory?.subcategoryImage} alt={allBrandsCategory?.SubCategory?.subcategoryName || "Sub category"} />
                    <div>
                      <Label>Sub Category</Label>
                      <EntityName>{formatValue(allBrandsCategory?.SubCategory?.subcategoryName)}</EntityName>
                      <Value>{formatValue(allBrandsCategory?.SubCategory?.description)}</Value>
                    </div>
                  </EntityCard>
                </EntityGrid>
              </DetailsSection>
            </ModalBody>
            <ModalActions>
              <SecondaryButton type="button" onClick={closeModal}>Close</SecondaryButton>
            </ModalActions>
          </Modal>
        </ModalOverlay>
      )}

      {imageModal && selectedProduct && (
        <ModalOverlay onClick={closeModal}>
          <Modal width="min(92vw, 760px)" onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalEyebrow>Product Images</ModalEyebrow>
                <ModalTitle>{selectedProduct.name}</ModalTitle>
              </div>
              <CloseButton type="button" aria-label="Close product images" onClick={closeModal}>
                <X size={18} />
              </CloseButton>
            </ModalHeader>
            <ImageBody>
              <LightboxImage src={selectedImages[lightboxIndex]} alt="Product preview" />
            </ImageBody>
            <ModalActions>
              <SecondaryButton type="button" onClick={prevImage} disabled={selectedImages.length < 2}>Prev</SecondaryButton>
              <SecondaryButton type="button" onClick={nextImage} disabled={selectedImages.length < 2}>Next</SecondaryButton>
              <PrimaryButton type="button" onClick={closeModal}>Close</PrimaryButton>
            </ModalActions>
          </Modal>
        </ModalOverlay>
      )}
    </ProductsPage>
  );
};

export default OperationList;

const PageHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PageSubtitle = styled.p`
  color: #64748b;
  font-size: 14px;
  margin: 0;
`;

const Wrapper = styled.div`
  padding: 18px;
  background: #f8fafc;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const Table = styled.div`
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 2fr) minmax(120px, 0.8fr) minmax(120px, 0.7fr) minmax(110px, 0.7fr) minmax(280px, 1fr);
  gap: 16px;
  align-items: center;
  padding: 13px 18px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;

  @media (max-width: 1100px) {
    display: none;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 2fr) minmax(120px, 0.8fr) minmax(120px, 0.7fr) minmax(110px, 0.7fr) minmax(280px, 1fr);
  gap: 16px;
  align-items: center;
  padding: 16px 18px;
  border-bottom: 1px solid #eef2f7;
  transition: background 0.2s ease;

  &:hover {
    background: #fbfdff;
  }

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 640px) {
    margin: 10px;
    padding: 14px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
  }
`;

const ProductCell = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`;

const ProductMedia = styled.button`
  position: relative;
  width: 74px;
  height: 74px;
  flex: 0 0 74px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 0;
  overflow: hidden;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
`;

const ImageCount = styled.span`
  position: absolute;
  right: 5px;
  bottom: 5px;
  padding: 2px 6px;
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.78);
  color: #fff;
  font-size: 11px;
  font-weight: 800;
`;

const ProductCopy = styled.div`
  min-width: 0;
`;

const ProductName = styled.div`
  color: #0f172a;
  font-size: 15px;
  font-weight: 800;
  margin-bottom: 5px;
`;

const ProductDesc = styled.div`
  color: #64748b;
  font-size: 13px;
  line-height: 1.45;
`;

const PriceStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const DiscountedPrice = styled.div`
  color: #0f766e;
  font-size: 15px;
  font-weight: 900;
`;

const Price = styled.div`
  color: #94a3b8;
  font-size: 12px;
  font-weight: 700;
  text-decoration: line-through;
`;

const MutedText = styled.span`
  color: #94a3b8;
  font-size: 12px;
  font-weight: 700;
`;

const InventoryCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    color: #0f172a;
    font-size: 16px;
  }

  span {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
  }
`;

const Status = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: fit-content;
  padding: 7px 10px;
  border-radius: 999px;
  background: ${({ verified }) => (verified ? "#d4f5e9" : "#fde4e4")};
  color: ${({ verified }) => (verified ? "#056d3b" : "#a61b1b")};
  font-size: 12px;
  font-weight: 800;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
    border-color: #94a3b8;
  }
`;

const ActionButton = styled.button`
  min-height: 36px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${({ danger }) => (danger ? "#fecaca" : "#99f6e4")};
  border-radius: 8px;
  background: ${({ danger }) => (danger ? "#fef2f2" : "#ecfeff")};
  color: ${({ danger }) => (danger ? "#dc2626" : "#0f766e")};
  padding: 0 12px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    background: ${({ danger }) => (danger ? "#fee2e2" : "#ccfbf1")};
  }
`;

const LoadingState = styled.div`
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 78px;
    height: 78px;
    object-fit: contain;
  }
`;

const EmptyState = styled.div`
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: #64748b;

  strong {
    color: #0f172a;
    font-size: 16px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99999999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(4px);

  @media (max-width: 480px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const Modal = styled.div`
  width: ${({ width }) => width || "460px"};
  max-width: 100%;
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);

  @media (max-width: 480px) {
    width: 100%;
    max-height: 92dvh;
    border-radius: 16px 16px 0 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 16px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const ModalEyebrow = styled.div`
  color: #0f766e;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 19px;
  font-weight: 900;
`;

const CloseButton = styled.button`
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #475569;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }
`;

const ModalBody = styled.div`
  padding: 20px 22px;
  overflow-y: auto;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 22px;
  border-top: 1px solid #e2e8f0;
  background: #fff;
`;

const PromptBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px;
  border: 1px solid ${({ verified }) => (verified ? "#99f6e4" : "#fecaca")};
  border-radius: 8px;
  background: ${({ verified }) => (verified ? "#ecfeff" : "#fef2f2")};
  color: ${({ verified }) => (verified ? "#0f766e" : "#dc2626")};
  font-size: 13px;
  font-weight: 800;
  line-height: 1.45;
`;

const FieldGroup = styled.div`
  margin-top: 16px;
`;

const FieldLabel = styled.label`
  display: block;
  color: #334155;
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 7px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 110px;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #0f172a;
  font-size: 14px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #30d5c8;
  }
`;

const SecondaryButton = styled.button`
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #f8fafc;
  }
`;

const PrimaryButton = styled.button`
  min-height: 40px;
  padding: 0 18px;
  border: 0;
  border-radius: 8px;
  background: ${({ danger }) => (danger ? "#dc2626" : "#30d5c8")};
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    background: ${({ danger }) => (danger ? "#b91c1c" : "#26a79d")};
  }
`;

const VariantTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 8px;

  th {
    padding: 12px;
    background: #f8fafc;
    color: #64748b;
    font-size: 12px;
    text-align: left;
    text-transform: uppercase;
  }

  td {
    padding: 12px;
    border-top: 1px solid #eef2f7;
    color: #334155;
    font-size: 14px;
  }
`;

const DetailsSection = styled.section`
  & + & {
    margin-top: 22px;
  }
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px;
  color: #0f172a;
  font-size: 14px;
  font-weight: 900;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  grid-column: ${({ wide }) => (wide ? "1 / -1" : "auto")};
  padding: 13px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
`;

const Label = styled.span`
  display: block;
  color: #64748b;
  font-size: 12px;
  font-weight: 900;
  margin-bottom: 5px;
  text-transform: uppercase;
`;

const Value = styled.span`
  display: block;
  color: #0f172a;
  font-size: 14px;
  line-height: 1.5;
  overflow-wrap: anywhere;
`;

const EntityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const EntityCard = styled.div`
  display: flex;
  gap: 12px;
  padding: 13px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
`;

const EntityImage = styled.img`
  width: 62px;
  height: 62px;
  flex: 0 0 62px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  object-fit: contain;
  padding: 6px;
`;

const EntityName = styled.div`
  color: #0f172a;
  font-size: 14px;
  font-weight: 900;
  margin-bottom: 4px;
`;

const ImageBody = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
  background: #0f172a;
`;

const LightboxImage = styled.img`
  width: min(100%, 620px);
  max-height: 68vh;
  object-fit: contain;
`;
